/**
 * Environment Configuration for Project X
 * Handles OAuth server URLs and other environment-specific settings
 */

export interface EnvironmentConfig {
  authServerUrl: string;
  clientId: string;
  clientSecret: string;
  redirectUri: string;
  scope: string;
  environment: 'local' | 'development' | 'production' | 'azure';
  isProduction: boolean;
  isDevelopment: boolean;
  isLocal: boolean;
  isCustomDomain: boolean;
  isAzureDomain: boolean;
  razorpayKeyId: string;
  recaptchaSiteKey: string;
}

/**
 * Detect current environment based on various factors
 */
function detectEnvironment(): 'local' | 'development' | 'production' | 'azure' {
  // Check React environment variables first (most reliable)
  const reactAppEnv = process.env.REACT_APP_ENV;
  
  // Add debug logging to see what we're actually getting
  console.log('🔍 Environment Detection Debug:', {
    reactAppEnv,
    nodeEnv: process.env.NODE_ENV,
    hostname: window.location.hostname,
    allReactAppVars: Object.keys(process.env).filter(k => k.startsWith('REACT_APP_'))
  });
  
  if (reactAppEnv) {
    return reactAppEnv as 'local' | 'development' | 'production' | 'azure';
  }

  // Fallback: Check if running in Azure (common Azure environment variables)
  const isAzure = !!(
    process.env.WEBSITE_SITE_NAME || 
    process.env.APPSETTING_WEBSITE_SITE_NAME ||
    window.location.hostname.includes('.azurewebsites.net')
  );

  if (isAzure) {
    console.warn('⚠️ No REACT_APP_ENV found, defaulting to production in Azure environment');
    // Default to production if in Azure but no REACT_APP_ENV is set
    return 'production';
  }

  // Check Node environment
  const nodeEnv = process.env.NODE_ENV;
  if (nodeEnv === 'production') {
    return 'production';
  }

  if (nodeEnv === 'development') {
    return 'development';
  }

  // Default to local for localhost
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    return 'local';
  }

  return 'local';
}

/**
 * Get environment-specific configuration
 */
function getEnvironmentConfig(): EnvironmentConfig {
  const environment = detectEnvironment();
  const isProduction = environment === 'production';
  const isDevelopment = environment === 'development';
  const isLocal = environment === 'local';
  
  // Domain detection
  const isAzureDomain = !!(
    window.location.hostname.includes('.azurewebsites.net') ||
    window.location.hostname.includes('.azure.com')
  );
  
  const isCustomDomain = !isAzureDomain && 
                        !window.location.hostname.includes('localhost') &&
                        !window.location.hostname.includes('127.0.0.1');

  // Base configuration that can be overridden by environment variables
  const baseConfig = {
    clientId: 'project-x-client',
    clientSecret: 'secret', // Note: In production, this should come from secure storage
    scope: 'openid profile email read write',
    environment,
    isProduction,
    isDevelopment,
    isLocal,
    isCustomDomain,
    isAzureDomain,
  };

  // Environment-specific OAuth server URLs
  let authServerUrl: string;
  let redirectUri: string;

  // Always prioritize environment variables over any fallbacks
  authServerUrl = process.env.REACT_APP_AUTH_SERVER_URL || '';
  redirectUri = process.env.REACT_APP_REDIRECT_URI || `${window.location.origin}/auth/callback`;

  // Only provide fallbacks for local development if no env vars are set
  if (!authServerUrl && environment === 'local') {
    authServerUrl = 'http://localhost:9000';
  }

  // Log what URLs are being used for debugging
  console.log('🔧 Auth Server Configuration:', {
    environment,
    authServerUrl,
    envVarValue: process.env.REACT_APP_AUTH_SERVER_URL,
    hostname: window.location.hostname,
  });

  // Return the configuration
  return {
    ...baseConfig,
    authServerUrl,
    redirectUri,
    clientId: process.env.REACT_APP_CLIENT_ID || baseConfig.clientId,
    clientSecret: process.env.REACT_APP_CLIENT_SECRET || baseConfig.clientSecret,
    scope: process.env.REACT_APP_SCOPE || baseConfig.scope,
    razorpayKeyId: process.env.REACT_APP_RAZORPAY_KEY_ID || 'rzp_test_default',
    recaptchaSiteKey: process.env.REACT_APP_RECAPTCHA_SITE_KEY || '',
  };
}

// Export the configuration
export const config = getEnvironmentConfig();

// Utility functions for environment checking
export const isLocal = () => config.isLocal;
export const isDevelopment = () => config.isDevelopment;
export const isProduction = () => config.isProduction;

// Utility function to get reCAPTCHA site key with validation
export const getRecaptchaSiteKey = (): string => {
  const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY || config.recaptchaSiteKey;
  
  if (!siteKey) {
    const errorMsg = `reCAPTCHA site key not configured. Environment: ${config.environment}, Domain: ${window.location.hostname}`;
    console.error('❌ reCAPTCHA Error:', {
      error: errorMsg,
      environment: config.environment,
      directEnvAccess: process.env.REACT_APP_RECAPTCHA_SITE_KEY ? 'present' : 'missing',
      configAccess: config.recaptchaSiteKey ? 'present' : 'missing',
      reactAppEnv: process.env.REACT_APP_ENV,
      hostname: window.location.hostname
    });
    throw new Error(errorMsg);
  }
  
  console.log('✅ reCAPTCHA site key loaded successfully');
  return siteKey;
};

// Debug logging (only in non-production)
if (!config.isProduction) {
  console.log('🔧 Environment Configuration:', {
    environment: config.environment,
    authServerUrl: config.authServerUrl,
    redirectUri: config.redirectUri,
    clientId: config.clientId,
    razorpayKeyId: config.razorpayKeyId,
    recaptchaSiteKey: config.recaptchaSiteKey ? '***configured***' : 'not set',
    hostname: window.location.hostname,
    nodeEnv: process.env.NODE_ENV,
    reactAppEnv: process.env.REACT_APP_ENV,
    isAzureDomain: config.isAzureDomain,
    isCustomDomain: config.isCustomDomain,
  });
}

// Debug Azure environment variables (only in Azure environments)
if (config.isAzureDomain) {
  console.log('🔧 Azure Environment Debug:', {
    REACT_APP_RECAPTCHA_SITE_KEY: process.env.REACT_APP_RECAPTCHA_SITE_KEY ? '***present***' : 'missing',
    REACT_APP_RAZORPAY_KEY_ID: process.env.REACT_APP_RAZORPAY_KEY_ID ? '***present***' : 'missing',
    REACT_APP_AUTH_SERVER_URL: process.env.REACT_APP_AUTH_SERVER_URL ? '***present***' : 'missing',
    WEBSITE_SITE_NAME: process.env.WEBSITE_SITE_NAME ? '***present***' : 'missing',
    allEnvKeys: Object.keys(process.env).filter(key => key.startsWith('REACT_APP_')),
  });
}

export default config;